import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { readTasksFromRealm, upsertRealmDocs, deleteRealmDoc, realmAvailable } from '../../realm';
import {
  writePendingTaskToRealm,
  applyPendingTaskUpdate,
  queuePendingTaskDelete,
  markTaskSynced,
  flushOutboundTaskQueue,
  overlayPendingTasks,
  isNetworkFailure,
} from '../../realm/outboundQueue';
import { taskWorkflowMessageIds } from '../../utils/workflowTaskUtils';

// Task status enum matching backend
export const TaskStatus = {
  PENDING: 'PENDING',
  PICK_TODAY: 'PICK_TODAY',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
};

const shouldReturnQueued = (optimistic, error) =>
  Boolean(optimistic) && (
    isNetworkFailure(error)
    || (typeof navigator !== 'undefined' && navigator.onLine === false)
  );

const taskFailureMessage = (error, fallback) => {
  const raw = error?.response?.data;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.replace(/^Error:\s*/i, '').trim();
  }
  if (raw && typeof raw === 'object') {
    const fromBody = raw.error || raw.message;
    if (fromBody) return String(fromBody).replace(/^Error:\s*/i, '').trim();
  }
  return fallback;
};

const patchTaskInState = (state, taskId, patch) => {
  if (!taskId || !patch || typeof patch !== 'object') return;
  const index = (state.tasks || []).findIndex((task) => task.id === taskId);
  if (index !== -1) {
    state.tasks[index] = { ...state.tasks[index], ...patch };
  }
  if (state.currentTask?.id === taskId) {
    state.currentTask = { ...state.currentTask, ...patch };
  }
  if (state.todayScreen?.tasksHydrated && Array.isArray(state.todayScreen.pickTodayTasks)) {
    state.todayScreen.pickTodayTasks = state.todayScreen.pickTodayTasks.map((task) => (
      task.id === taskId ? { ...task, ...patch } : task
    ));
  }
};

const restoreExistingTask = async (taskId, existing) => {
  if (!existing?.id) return;
  try {
    await markTaskSynced(taskId, existing);
  } catch {
    // ignore rollback failure
  }
};

const persistTasks = (tasks) => {
  const list = Array.isArray(tasks) ? tasks.filter(Boolean) : (tasks ? [tasks] : []);
  if (!list.length) return;
  upsertRealmDocs('Task', list).catch(() => {});
};

const shouldSkipTaskListApi = (filters = {}) =>
  !filters.force && !filters.targetUserId;

const readLocalTaskList = async (filters = {}) => {
  const { force, ...rest } = filters;
  const cached = await readTasksFromRealm();
  const local = filterCachedTasks(cached || [], rest);
  const overlaid = await overlayPendingTasks(local);
  if (overlaid.length) return overlaid;
  return filterCachedTasks(await overlayPendingTasks([]), rest);
};

export const broadcastTaskUpdated = (task, action = 'updated') => {
  if (typeof window === 'undefined' || !task?.id) return;
  window.dispatchEvent(new CustomEvent('workloop:task-updated', {
    detail: { task, action },
  }));
};

const taskStatusOf = (task) => {
  const status = task?.status || task?.statusString || '';
  return typeof status === 'string' ? status : String(status);
};

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
};

const emitProductivityEntry = (entry) => {
  if (typeof window === 'undefined' || !entry) return;
  window.dispatchEvent(new CustomEvent('workloop:productivity-entry', { detail: entry }));
};

const sameTaskUserId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

const taskDelegateIds = (task) => (
  Array.isArray(task?.delegateId)
    ? task.delegateId.filter(Boolean)
    : (task?.delegateId ? [task.delegateId] : [])
);

const isDelegatedToOthers = (task, userId) =>
  taskDelegateIds(task).some((id) => !sameTaskUserId(id, userId));

const isUnmappedCachedTask = (task) => !String(task?.mapTaskId || '').trim();

const isMyOwnTask = (task, userId) => (
  sameTaskUserId(task?.createdByUserId, userId)
  && isUnmappedCachedTask(task)
  && !isDelegatedToOthers(task, userId)
);

const filterCachedTasks = (tasks, { messageId, mapTaskId, status, type, targetUserId } = {}) => {
  const list = Array.isArray(tasks) ? tasks : [];
  return list.filter((task) => {
    if (messageId) {
      if (!taskWorkflowMessageIds(task).includes(String(messageId))) return false;
    }
    if (mapTaskId && task.mapTaskId !== mapTaskId) return false;
    if (status && taskStatusOf(task) !== status) return false;
    if (type === 'my') {
      return isMyOwnTask(task, targetUserId);
    }
    if (targetUserId) {
      const delegates = taskDelegateIds(task);
      const isOwner = sameTaskUserId(task.createdByUserId, targetUserId);
      const isDelegate = delegates.some((id) => sameTaskUserId(id, targetUserId));
      if (!isOwner && !isDelegate) return false;
    }
    if (type === 'delegated') {
      const delegates = taskDelegateIds(task);
      const viewerId = targetUserId || task?.viewerUserId;
      return delegates.some((id) => sameTaskUserId(id, viewerId))
        && !sameTaskUserId(task.createdByUserId, viewerId)
        && delegates.some((id) => !sameTaskUserId(id, task.createdByUserId));
    }
    if (type === 'delegated-by-me') {
      const delegates = taskDelegateIds(task);
      return delegates.some((id) => !sameTaskUserId(id, task.createdByUserId));
    }
    return true;
  });
};

// Async thunks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async ({ messageId, mapTaskId, status, type, targetUserId, force } = {}, { rejectWithValue }) => {
    const filters = { messageId, mapTaskId, status, type, targetUserId, force };
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    try {
      if (shouldSkipTaskListApi(filters) && (await realmAvailable())) {
        const local = await readLocalTaskList(filters);
        if (local.length || (!messageId && !mapTaskId) || offline) {
          return local;
        }
      }
      if (offline) {
        return readLocalTaskList(filters);
      }

      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (messageId) params.append('messageId', messageId);
      if (mapTaskId) params.append('mapTaskId', mapTaskId);
      if (status) params.append('status', status);
      if (type) params.append('type', type);
      if (targetUserId) params.append('targetUserId', targetUserId);

      const url = `${API_URL}/tasks${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      persistTasks(Array.isArray(response.data) ? response.data : []);
      const fromApi = await overlayPendingTasks(Array.isArray(response.data) ? response.data : []);
      if (messageId && (await realmAvailable())) {
        const cached = await readTasksFromRealm();
        const localMatches = filterCachedTasks(cached || [], filters);
        const byId = new Map();
        [...fromApi, ...localMatches].forEach((task) => {
          if (task?.id && !byId.has(task.id)) byId.set(task.id, task);
        });
        return Array.from(byId.values());
      }
      return fromApi;
    } catch (error) {
      const cached = await readTasksFromRealm();
      if (cached && cached.length) {
        const local = filterCachedTasks(cached, filters);
        return overlayPendingTasks(local);
      }
      const pendingOnly = await overlayPendingTasks([]);
      if (pendingOnly.length) {
        return filterCachedTasks(pendingOnly, filters);
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch tasks');
    }
  }
);

export const fetchMyTasks = createAsyncThunk(
  'tasks/fetchMyTasks',
  async (targetUserId, { rejectWithValue, getState }) => {
    const userId = targetUserId || getState()?.auth?.user?.id;
    const mine = (list) => filterCachedTasks(list || [], { type: 'my', targetUserId: userId });
    try {
      if (!targetUserId && (await realmAvailable())) {
        return mine(await readLocalTaskList({ type: 'my', targetUserId: userId }));
      }
      const token = localStorage.getItem('token');
      const url = targetUserId 
        ? `${API_URL}/tasks?type=my&targetUserId=${encodeURIComponent(targetUserId)}`
        : `${API_URL}/tasks?type=my`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fromApi = Array.isArray(response.data) ? response.data : [];
      if (!targetUserId) {
        upsertRealmDocs('Task', fromApi);
      }
      persistTasks(fromApi);
      return mine(fromApi);
    } catch (error) {
      if (!targetUserId) {
        const cached = await readTasksFromRealm();
        if (cached && cached.length) return mine(cached);
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch my tasks');
    }
  }
);

export const fetchDelegatedTasks = createAsyncThunk(
  'tasks/fetchDelegatedTasks',
  async (_, { rejectWithValue, getState }) => {
    const userId = getState()?.auth?.user?.id;
    try {
      if (await realmAvailable()) {
        return overlayPendingTasks(
          filterCachedTasks(await readTasksFromRealm() || [], { type: 'delegated', targetUserId: userId }),
        );
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks?type=delegated`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      persistTasks(Array.isArray(response.data) ? response.data : []);
      return response.data;
    } catch (error) {
      const cached = await readTasksFromRealm();
      if (cached && cached.length) {
        return filterCachedTasks(cached, { type: 'delegated', targetUserId: userId });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch delegated tasks');
    }
  }
);

export const fetchTasksIDelegated = createAsyncThunk(
  'tasks/fetchTasksIDelegated',
  async (_, { rejectWithValue }) => {
    try {
      if (await realmAvailable()) {
        return overlayPendingTasks(
          filterCachedTasks(await readTasksFromRealm() || [], { type: 'delegated-by-me' }),
        );
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks?type=delegated-by-me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      persistTasks(Array.isArray(response.data) ? response.data : []);
      return response.data;
    } catch (error) {
      const cached = await readTasksFromRealm();
      if (cached && cached.length) {
        return filterCachedTasks(cached, { type: 'delegated-by-me' });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch tasks I delegated');
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'tasks/fetchTaskById',
  async (taskId, { rejectWithValue }) => {
    try {
      if (await realmAvailable()) {
        const cached = await readTasksFromRealm();
        const found = (cached || []).find((t) => t.id === taskId || t._id === taskId);
        if (found) return found;
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      persistTasks(response.data);
      broadcastTaskUpdated(response.data, 'created');
      return response.data;
    } catch (error) {
      const cached = await readTasksFromRealm();
      const found = (cached || []).find((t) => t.id === taskId || t._id === taskId);
      if (found) return found;
      return rejectWithValue(error.response?.data || 'Failed to fetch task');
    }
  }
);

export const fetchTasksByMapTaskId = createAsyncThunk(
  'tasks/fetchTasksByMapTaskId',
  async (mapTaskId, { rejectWithValue }) => {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    try {
      if ((await realmAvailable()) && offline) {
        const cached = await readTasksFromRealm();
        return overlayPendingTasks(filterCachedTasks(cached || [], { mapTaskId }));
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/map-task/${mapTaskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      persistTasks(Array.isArray(response.data) ? response.data : []);
      return overlayPendingTasks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      const cached = await readTasksFromRealm();
      if (cached && cached.length) {
        return overlayPendingTasks(filterCachedTasks(cached, { mapTaskId }));
      }
      const pendingOnly = await overlayPendingTasks([]);
      if (pendingOnly.length) {
        return filterCachedTasks(pendingOnly, { mapTaskId });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch tasks by mapTaskId');
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData, { getState, rejectWithValue }) => {
    const userId = getState().auth?.user?.id;
    let localTask = null;
    try {
      localTask = await writePendingTaskToRealm({ taskData, userId });
    } catch (err) {
      console.warn('[tasks] local write failed', err);
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false && localTask) {
      broadcastTaskUpdated(localTask, 'created');
      return localTask;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/tasks`,
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      if (localTask?.id) {
        await markTaskSynced(localTask.id, response.data);
      } else {
        persistTasks(response.data);
      }
      broadcastTaskUpdated(response.data, 'created');
      return response.data;
    } catch (error) {
      if (shouldReturnQueued(localTask, error)) {
        broadcastTaskUpdated(localTask, 'created');
        return localTask;
      }
      if (localTask?.id && String(localTask.id).startsWith('pending-')) {
        try {
          await markTaskSynced(localTask.id, null);
        } catch {
          // ignore rollback failure
        }
      }
      return rejectWithValue(error.response?.data || 'Failed to create task');
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ taskId, taskData }, { getState, rejectWithValue }) => {
    const state = getState();
    const existing = (state.tasks?.tasks || []).find((task) => task.id === taskId)
      || state.tasks?.currentTask;
    let localTask = null;
    try {
      localTask = await applyPendingTaskUpdate({
        taskId,
        patch: taskData,
        existing: existing?.id === taskId ? existing : null,
        userId: state.auth?.user?.id,
      });
    } catch (err) {
      console.warn('[tasks] local update failed', err);
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false && localTask) {
      broadcastTaskUpdated(localTask);
      return localTask;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/tasks/${taskId}`,
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      return response.data;
    } catch (error) {
      if (shouldReturnQueued(localTask, error)) {
        broadcastTaskUpdated(localTask);
        return localTask;
      }
      await restoreExistingTask(taskId, existing);
      return rejectWithValue(taskFailureMessage(error, 'Failed to update task'));
    }
  }
);

export const mapTaskToWorkflow = createAsyncThunk(
  'tasks/mapTaskToWorkflow',
  async ({ taskId, messageId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/tasks/${taskId}/workflows/${messageId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      persistTasks([response.data]);
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      return response.data;
    } catch (error) {
      const message = error.response?.data || 'Failed to map workflow';
      return rejectWithValue(message);
    }
  }
);

export const unmapTaskFromWorkflow = createAsyncThunk(
  'tasks/unmapTaskFromWorkflow',
  async ({ taskId, messageId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/tasks/${taskId}/workflows/${messageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      persistTasks([response.data]);
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      return response.data;
    } catch (error) {
      const message = error.response?.data || 'Failed to unmap workflow';
      return rejectWithValue(message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskIdOrTask, { getState, rejectWithValue }) => {
    const taskId = typeof taskIdOrTask === 'object' && taskIdOrTask
      ? (taskIdOrTask.id || taskIdOrTask._id)
      : taskIdOrTask;
    const state = getState();
    const userId = state.auth?.user?.id;
    const existing = (typeof taskIdOrTask === 'object' && taskIdOrTask?.id ? taskIdOrTask : null)
      || (state.tasks?.tasks || []).find((task) => task.id === taskId);
    const isMappedChat = Boolean(existing?.mapTaskId && existing?.messageId);
    if (!isMappedChat && existing?.createdByUserId && userId
      && String(existing.createdByUserId).toLowerCase() !== String(userId).toLowerCase()) {
      return rejectWithValue('You don\'t have permission to delete this task. Only the task creator can delete tasks.');
    }

    let localTask = null;
    try {
      localTask = await queuePendingTaskDelete({ taskId, existing, userId });
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to delete task');
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false && localTask) {
      return taskId;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      await markTaskSynced(taskId, null);
      deleteRealmDoc('Task', taskId).catch(() => {});
      return taskId;
    } catch (error) {
      if (shouldReturnQueued(localTask, error)) {
        return taskId;
      }
      return rejectWithValue(error.response?.data || 'Failed to delete task');
    }
  }
);

export const markTaskAsCompleted = createAsyncThunk(
  'tasks/markTaskAsCompleted',
  async (payload, { getState, rejectWithValue }) => {
    const taskId = typeof payload === 'string' ? payload : payload?.id;
    const productivityDate = typeof payload === 'object' ? payload?.productivityDate : null;
    const state = getState();
    const existing = (typeof payload === 'object' && payload?.id ? payload : null)
      || (state.tasks?.tasks || []).find((task) => task.id === taskId);
    const patch = { status: TaskStatus.COMPLETED, completionPercentage: 100, timezone: browserTimezone() };
    const completionTimeInMinutes = typeof payload === 'object'
      ? Number(payload?.completionTimeInMinutes)
      : NaN;
    if (Number.isFinite(completionTimeInMinutes) && completionTimeInMinutes > 0) {
      patch.completionTimeInMinutes = completionTimeInMinutes;
    }
    let localTask = null;
    try {
      localTask = await applyPendingTaskUpdate({
        taskId,
        patch,
        existing,
        userId: state.auth?.user?.id,
      });
    } catch (err) {
      console.warn('[tasks] local complete failed', err);
    }

    const fromPercent = (() => {
      const raw = Number(existing?.completionPercentage);
      if (Number.isFinite(raw)) return Math.max(0, Math.min(100, Math.round(raw)));
      return String(existing?.status || '').toUpperCase() === 'COMPLETED' ? 100 : 0;
    })();
    if (100 > fromPercent && (existing || localTask)) {
      try {
        const { buildProductivityEntry } = await import('../../utils/productivityEntryUtils');
        const { upsertRealmDocs } = await import('../../realm');
        const entryDate = productivityDate || new Date().toLocaleDateString('en-CA');
        const entry = buildProductivityEntry({
          task: { ...(existing || {}), ...(localTask || {}), ...patch },
          fromPercent,
          toPercent: 100,
          userId: state.auth?.user?.id,
          dateStr: entryDate,
        });
        if (entry) {
          emitProductivityEntry(entry);
          try {
            await upsertRealmDocs('ProductivityEntry', entry);
          } catch (realmErr) {
            console.warn('[tasks] realm productivity entry failed', realmErr);
          }
        }
      } catch (err) {
        console.warn('[tasks] local productivity entry failed', err);
      }
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false && localTask) {
      broadcastTaskUpdated(localTask);
      return localTask;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/tasks/${taskId}`,
        patch,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      if (100 > fromPercent) {
        try {
          const { buildProductivityEntry } = await import('../../utils/productivityEntryUtils');
          const entryDate = productivityDate || new Date().toLocaleDateString('en-CA');
          const entry = buildProductivityEntry({
            task: response.data,
            fromPercent,
            toPercent: 100,
            userId: state.auth?.user?.id,
            dateStr: entryDate,
          });
          if (entry) emitProductivityEntry(entry);
        } catch {
          // ignore
        }
      }
      return response.data;
    } catch (error) {
      if (shouldReturnQueued(localTask, error)) {
        broadcastTaskUpdated(localTask);
        return localTask;
      }
      await restoreExistingTask(taskId, existing);
      return rejectWithValue(taskFailureMessage(error, 'Failed to mark task as completed'));
    }
  }
);

export const undoTaskCompletion = createAsyncThunk(
  'tasks/undoTaskCompletion',
  async (taskId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/tasks/${taskId}/undo-complete`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('workloop:productivity-entry-removed', {
          detail: { taskId },
        }));
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(taskFailureMessage(error, 'Failed to undo task completion'));
    }
  }
);

export const updateTaskProgress = createAsyncThunk(
  'tasks/updateTaskProgress',
  async ({ taskId, completionPercentage, task, completionTimeInMinutes, productivityDate }, { getState, rejectWithValue }) => {
    const state = getState();
    const existing = task || (state.tasks?.tasks || []).find((item) => item.id === taskId);
    const fromPercent = (() => {
      const raw = Number(existing?.completionPercentage);
      if (Number.isFinite(raw)) return Math.max(0, Math.min(100, Math.round(raw)));
      return String(existing?.status || '').toUpperCase() === 'COMPLETED' ? 100 : 0;
    })();
    const pct = Math.max(0, Math.min(100, Math.round(Number(completionPercentage) || 0)));
    const patch = { completionPercentage: pct, timezone: browserTimezone() };
    const minutes = Number(completionTimeInMinutes);
    if (Number.isFinite(minutes) && minutes > 0) {
      patch.completionTimeInMinutes = minutes;
    }
    let localTask = null;
    try {
      localTask = await applyPendingTaskUpdate({
        taskId,
        patch,
        existing,
        userId: state.auth?.user?.id,
      });
    } catch (err) {
      console.warn('[tasks] local progress failed', err);
    }

    if (pct > fromPercent && (existing || localTask)) {
      try {
        const { buildProductivityEntry } = await import('../../utils/productivityEntryUtils');
        const { upsertRealmDocs } = await import('../../realm');
        const entryDate = productivityDate || new Date().toLocaleDateString('en-CA');
        const entry = buildProductivityEntry({
          task: { ...(existing || {}), ...(localTask || {}), ...patch },
          fromPercent,
          toPercent: pct,
          userId: state.auth?.user?.id,
          dateStr: entryDate,
        });
        if (entry) {
          emitProductivityEntry(entry);
          try {
            await upsertRealmDocs('ProductivityEntry', entry);
          } catch (realmErr) {
            console.warn('[tasks] realm productivity entry failed', realmErr);
          }
        }
      } catch (err) {
        console.warn('[tasks] local productivity entry failed', err);
      }
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false && localTask) {
      broadcastTaskUpdated(localTask);
      return localTask;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/tasks/${taskId}`,
        patch,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        }
      );
      await markTaskSynced(taskId, response.data);
      broadcastTaskUpdated(response.data);
      if (pct > fromPercent) {
        try {
          const { buildProductivityEntry } = await import('../../utils/productivityEntryUtils');
          const entryDate = productivityDate || new Date().toLocaleDateString('en-CA');
          const entry = buildProductivityEntry({
            task: response.data,
            fromPercent,
            toPercent: pct,
            userId: state.auth?.user?.id,
            dateStr: entryDate,
          });
          if (entry) emitProductivityEntry(entry);
        } catch {
          // ignore
        }
      }
      return response.data;
    } catch (error) {
      if (shouldReturnQueued(localTask, error)) {
        broadcastTaskUpdated(localTask);
        return localTask;
      }
      await restoreExistingTask(taskId, existing);
      return rejectWithValue(taskFailureMessage(error, 'Failed to update task progress'));
    }
  }
);

export const flushOutboundTasks = createAsyncThunk(
  'tasks/flushOutboundTasks',
  async () => {
    const result = await flushOutboundTaskQueue();
    return result.synced || [];
  }
);

export const todayScreenOwnerKey = (userId, targetUserId, date) =>
  `${userId || ''}|${targetUserId || ''}|${date || ''}`;

const emptyTodayScreen = () => ({
  ownerKey: '',
  pickTodayTasks: [],
  assignedWorkflows: [],
  completedAssignedWorkflows: [],
  tasksHydrated: false,
  workflowsHydrated: false,
});

export const fetchDailyAnalytics = createAsyncThunk(
  'tasks/fetchDailyAnalytics',
  async ({ date, targetUserId, force } = {}, { rejectWithValue, getState }) => {
    const analyticsKey = `${date || ''}|${targetUserId || ''}`;
    const existing = getState().tasks;
    if (!force && existing.dailyAnalytics && existing.dailyAnalyticsKey === analyticsKey) {
      return existing.dailyAnalytics;
    }
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const buildLocal = async () => {
      if (targetUserId) return null;
      try {
        const { readTasksFromRealm, readRemindersFromRealm, readProductivityEntriesFromRealm } = await import('../../realm');
        const { buildLocalDailyAnalytics } = await import('../../utils/localDailyAnalytics');
        const [tasks, reminders, productivityEntries] = await Promise.all([
          readTasksFromRealm(),
          readRemindersFromRealm(),
          readProductivityEntriesFromRealm(),
        ]);
        const todayStr = new Date().toLocaleDateString('en-CA');
        return buildLocalDailyAnalytics({
          tasks: tasks || [],
          reminders: reminders || [],
          productivityEntries: productivityEntries || [],
          dateStr: date,
          viewingToday: String(date) === todayStr,
        });
      } catch {
        return null;
      }
    };

    if (offline) {
      const local = await buildLocal();
      if (local) return local;
      return rejectWithValue('Failed to fetch daily analytics');
    }

    try {
      const token = localStorage.getItem('token');
      let timezone = 'Asia/Kolkata';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || timezone;
      } catch (_) {
        // keep default
      }
      const params = new URLSearchParams({ date, timezone });
      if (targetUserId) params.set('targetUserId', targetUserId);
      const response = await axios.get(`${API_URL}/tasks/analytics/daily?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      const local = await buildLocal();
      if (local) return local;
      return rejectWithValue(error.response?.data || 'Failed to fetch daily analytics');
    }
  }
);

const initialState = {
  tasks: [],
  currentTask: null,
  dailyAnalytics: null,
  dailyAnalyticsKey: '',
  todayScreen: emptyTodayScreen(),
  loading: false,
  error: null,
  fabTaskNotifications: [],
  taskArrivalToasts: [],
  unseenTaskDelegationCount: 0,
};

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentTask: (state, action) => {
      state.currentTask = action.payload;
    },
    clearCurrentTask: (state) => {
      state.currentTask = null;
    },
    setTodayPickTasksCache: (state, action) => {
      const { ownerKey, tasks } = action.payload || {};
      if (!ownerKey) return;
      if (state.todayScreen.ownerKey && state.todayScreen.ownerKey !== ownerKey) {
        state.todayScreen = emptyTodayScreen();
      }
      state.todayScreen.ownerKey = ownerKey;
      state.todayScreen.pickTodayTasks = Array.isArray(tasks) ? tasks : [];
      state.todayScreen.tasksHydrated = true;
    },
    setTodayWorkflowsCache: (state, action) => {
      const { ownerKey, assignedWorkflows, completedAssignedWorkflows } = action.payload || {};
      if (!ownerKey) return;
      if (state.todayScreen.ownerKey && state.todayScreen.ownerKey !== ownerKey) {
        state.todayScreen = emptyTodayScreen();
      }
      state.todayScreen.ownerKey = ownerKey;
      if (assignedWorkflows !== undefined) {
        state.todayScreen.assignedWorkflows = Array.isArray(assignedWorkflows) ? assignedWorkflows : [];
      }
      if (completedAssignedWorkflows !== undefined) {
        state.todayScreen.completedAssignedWorkflows = Array.isArray(completedAssignedWorkflows)
          ? completedAssignedWorkflows
          : [];
      }
      state.todayScreen.workflowsHydrated = true;
    },
    upsertLiveTask: (state, action) => {
      const task = action.payload;
      if (!task?.id) return;
      const index = (state.tasks || []).findIndex((row) => row.id === task.id);
      if (index === -1) state.tasks = [...(state.tasks || []), task];
      else state.tasks[index] = { ...state.tasks[index], ...task };
    },
    applyLiveTaskToTodayCache: (state, action) => {
      const task = action.payload?.task;
      const eventType = String(action.payload?.eventType || '').toUpperCase();
      if (!task?.id) return;
      const taskIndex = (state.tasks || []).findIndex((row) => row.id === task.id);
      if (eventType === 'TASK_DELETED') {
        state.tasks = (state.tasks || []).filter((row) => row.id !== task.id);
      } else if (taskIndex === -1) {
        state.tasks = [...(state.tasks || []), task];
      } else {
        state.tasks[taskIndex] = { ...state.tasks[taskIndex], ...task };
      }
      if (!state.todayScreen.tasksHydrated) return;
      const status = task.status || task.statusString;
      const isToday = status === TaskStatus.PICK_TODAY
        || (status === TaskStatus.PENDING && String(task.messageId || '').trim() && String(task.title || '').trim());
      const list = Array.isArray(state.todayScreen.pickTodayTasks) ? state.todayScreen.pickTodayTasks : [];
      if (eventType === 'TASK_DELETED' || !isToday) {
        state.todayScreen.pickTodayTasks = list.filter((item) => item.id !== task.id);
        return;
      }
      const exists = list.some((item) => item.id === task.id);
      state.todayScreen.pickTodayTasks = exists
        ? list.map((item) => (item.id === task.id ? { ...item, ...task } : item))
        : [...list, task];
    },
    receiveTaskDelegatedEvent: (state, action) => {
      const { task, delegatorName, assignmentKind } = action.payload;
      if (!task?.id) return;

      const fabKey = `TASK_DELEGATED-${task.id}`;
      const exists = state.fabTaskNotifications.some((n) => n.fabKey === fabKey);
      if (!exists) {
        state.fabTaskNotifications.unshift({
          fabKey,
          task,
          delegatorName: delegatorName || 'Someone',
          assignmentKind: assignmentKind === 'ASSIGNED' ? 'ASSIGNED' : 'DELEGATED',
          receivedAt: new Date().toISOString(),
        });
        if (state.fabTaskNotifications.length > 20) {
          state.fabTaskNotifications.length = 20;
        }
        state.unseenTaskDelegationCount += 1;
      }

      const alreadyToasting = state.taskArrivalToasts.some((toast) => toast.task?.id === task.id);
      if (!alreadyToasting) {
        state.taskArrivalToasts.unshift({
          id: `${fabKey}-${Date.now()}`,
          task,
          delegatorName: delegatorName || 'Someone',
          assignmentKind: assignmentKind === 'ASSIGNED' ? 'ASSIGNED' : 'DELEGATED',
        });
        if (state.taskArrivalToasts.length > 20) {
          state.taskArrivalToasts.length = 20;
        }
      }
    },
    dismissTaskArrivalToast: (state, action) => {
      state.taskArrivalToasts = state.taskArrivalToasts.filter((toast) => toast.id !== action.payload);
    },
    clearTaskArrivalToasts: (state) => {
      state.taskArrivalToasts = [];
    },
    markTaskDelegationsSeen: (state) => {
      state.unseenTaskDelegationCount = 0;
    },
    dismissFabTaskNotification: (state, action) => {
      const fabKey = action.payload;
      state.fabTaskNotifications = state.fabTaskNotifications.filter(
        (n) => n.fabKey !== fabKey
      );
    },
    clearAllFabTaskNotifications: (state) => {
      state.fabTaskNotifications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state, action) => {
        const filters = action.meta?.arg || {};
        if (filters.messageId || filters.mapTaskId || (state.tasks || []).length) {
          state.error = null;
          return;
        }
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        const incoming = Array.isArray(action.payload) ? action.payload : [];
        const filters = action.meta?.arg || {};
        if (filters.messageId || filters.mapTaskId) {
          const byId = new Map((state.tasks || []).map((task) => [task.id, task]));
          incoming.forEach((task) => {
            if (task?.id) byId.set(task.id, task);
          });
          state.tasks = Array.from(byId.values());
        } else {
          state.tasks = incoming;
        }
        state.error = null;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch task by ID
      .addCase(fetchTaskById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.currentTask = action.payload;
        state.error = null;
        const task = action.payload;
        if (!task?.id) return;
        const index = (state.tasks || []).findIndex((row) => row.id === task.id);
        if (index === -1) state.tasks = [...(state.tasks || []), task];
        else state.tasks[index] = { ...state.tasks[index], ...task };
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Create task
      .addCase(createTask.pending, (state) => {
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        const created = action.payload;
        if (created?.id) {
          const index = state.tasks.findIndex((task) => task.id === created.id);
          if (index !== -1) {
            state.tasks[index] = created;
          } else {
            state.tasks.push(created);
          }
        }
        state.error = null;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update task
      .addCase(updateTask.pending, (state, action) => {
        state.error = null;
        const { taskId, taskData } = action.meta.arg || {};
        patchTaskInState(state, taskId, taskData);
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        } else if (action.payload?.id) {
          state.tasks.push(action.payload);
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(mapTaskToWorkflow.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        } else {
          state.tasks.push(action.payload);
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      .addCase(unmapTaskFromWorkflow.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        } else {
          state.tasks.push(action.payload);
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter((task) => task.id !== action.payload);
        if (state.currentTask?.id === action.payload) {
          state.currentTask = null;
        }
        state.error = null;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Mark task as completed
      .addCase(markTaskAsCompleted.pending, (state) => {
        state.error = null;
      })
      .addCase(markTaskAsCompleted.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
        state.error = null;
      })
      .addCase(undoTaskCompletion.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        } else {
          state.tasks.push(action.payload);
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
      })
      .addCase(markTaskAsCompleted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTaskProgress.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTaskProgress.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.currentTask?.id === action.payload.id) {
          state.currentTask = action.payload;
        }
        state.error = null;
      })
      .addCase(updateTaskProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(flushOutboundTasks.fulfilled, (state, action) => {
        const replacements = action.payload || [];
        replacements.forEach(({ localId, serverTask, deleted }) => {
          if (deleted || !serverTask) {
            state.tasks = state.tasks.filter(
              (task) => task.id !== localId && task._id !== localId
            );
            if (state.currentTask?.id === localId) {
              state.currentTask = null;
            }
            return;
          }
          const index = state.tasks.findIndex(
            (task) => task.id === localId || task.id === serverTask.id
          );
          if (index !== -1) {
            state.tasks[index] = serverTask;
          } else {
            state.tasks.push(serverTask);
          }
        });
      })
      // Fetch my tasks
      .addCase(fetchMyTasks.pending, (state) => {
        if (!(state.tasks || []).length) state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
        state.error = null;
      })
      .addCase(fetchMyTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch delegated tasks
      .addCase(fetchDelegatedTasks.pending, (state) => {
        if (!(state.tasks || []).length) state.loading = true;
        state.error = null;
      })
      .addCase(fetchDelegatedTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
        state.error = null;
      })
      .addCase(fetchDelegatedTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch tasks I delegated
      .addCase(fetchTasksIDelegated.pending, (state) => {
        if (!(state.tasks || []).length) state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasksIDelegated.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
        state.error = null;
      })
      .addCase(fetchTasksIDelegated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch daily analytics
      .addCase(fetchDailyAnalytics.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchDailyAnalytics.fulfilled, (state, action) => {
        state.dailyAnalytics = action.payload;
        const arg = action.meta?.arg || {};
        state.dailyAnalyticsKey = `${arg.date || ''}|${arg.targetUserId || ''}`;
        state.error = null;
      })
      .addCase(fetchDailyAnalytics.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentTask,
  clearCurrentTask,
  setTodayPickTasksCache,
  setTodayWorkflowsCache,
  applyLiveTaskToTodayCache,
  upsertLiveTask,
  receiveTaskDelegatedEvent,
  dismissTaskArrivalToast,
  clearTaskArrivalToasts,
  markTaskDelegationsSeen,
  dismissFabTaskNotification,
  clearAllFabTaskNotifications,
} = taskSlice.actions;
export default taskSlice.reducer;


